<template>
    <v-row justify="end" class="ma-0 pa-0">
        <v-dialog
                v-model="dialog"
                hide-overlay
                width="auto"
                :fullscreen="$vuetify.breakpoint.xsOnly"
                transition="dialog-bottom-transition"
        >
            <v-card style="min-width: 450px" class="card">
                <v-container class="container">
                    <template>
                        <div class="headSection">
                            <div class="centered-child">Export Settings</div>
                            <div class="right-aligned-child" @click="close">X</div>
                        </div>
                        <p class="headText">
                            Select the fields to include in the export file. You can rename them if needed or leave it empty to use the original name
                        </p>
                        
                        <!-- Breakdown option -->
                        <v-card v-if="showBreakdownOption" class="mb-4 pa-3" outlined>
                          <v-checkbox
                            v-model="enableBreakdown"
                            :label="`Include breakdown by ${breakdownLabel}`"
                            color="primary"
                            hide-details
                            dense
                          ></v-checkbox>
                          <p class="caption mt-2 mb-0">
                            This will add {{ breakdownLabel }} column to the export, showing data breakdown for each {{ reportTypeLabel }}
                          </p>
                        </v-card>
                        
                        <v-data-table
                            :items="items"
                            :headers="fileHeaders"
                            class="elevation-1"
                            :items-per-page="-1"
                            disable-sort
                        >
                            <template v-for="header in fileHeaders" :slot="`item.${[header.value]}`" slot-scope="{item}">
                                <template v-if="header.value === 'include'">
                                    <div class="d-flex justify-center">
                                        <v-checkbox
                                                v-model="item.isInclude"
                                                color="primary"
                                                hide-details
                                        ></v-checkbox>
                                    </div>
                                </template>
                                <template v-else-if="header.value ==='renamed_to'">
                                    <v-text-field
                                            v-model="item.renamed_to"
                                            single-line
                                            hide-details
                                    ></v-text-field>
                                </template>
                                <template v-else>{{item[header['value']]}}</template>
                            </template>
                        </v-data-table>
                    </template>
                    <div class="btn-section">
                        <v-btn @click="download" :loading="loading" :disabled="loading">
                            Export to Email
                        </v-btn>
                    </div>
                </v-container>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
    import { mapGetters } from 'vuex'
    import store from "@/store";

    export default {
        data: () => ({
            dialog: false,
            loading: false,
            items: [],
            fileHeaders: [
                { text: 'Include', value: 'include', align: 'center', width: '10%'},
                { text: 'Column', value: 'column', align: 'center',  width: '25%'},
                { text: 'Renamed To', value: 'renamed_to', align: 'center', width: '25%' },
                { text: 'Preview', value: 'preview', align: 'center', width: '40%' }
            ],
            firstRow: {},
            reportType: null,
            enableBreakdown: false,
        }),
        props: {
            isShow: Boolean,
            headers: {
                type: Array,
                default: []
            },
            example: {
                type: Object,
                default() {
                    return {}
                }
            }
        },
        async mounted() {
            this.dialog = this.isShow;
        },
        watch: {
          isShow() {
              this.dialog = !this.dialog;
              this.setHeadersAndExample();

              const isLidOrCidHere = this.items.find(i => {
                  if (['lid', 'cid'].includes(i.column)) {
                      return i;
                  }
              });
              if (isLidOrCidHere) {
                  const extraFields = [
                      'campaign_id',
                      'campaign',
                      'affiliate_id',
                      'affiliate',
                      'geo',
                      'ip',
                      'click_date',
                      'conversion_date',
                      'redirect_url',
                      'referrer',
                      'sub_campaign',
                      'is_fired',
                      'payin',
                      'payout',
                  ];

                  const hasLid = this.items.some(i => i.column === 'lid');
                  const hasCid = this.items.some(i => i.column === 'cid');

                  if (hasLid && !extraFields.includes('cid')) {
                      extraFields.unshift('cid');
                  } else if (hasCid && !extraFields.includes('lid')) {
                      extraFields.unshift('lid');
                  }

                  const fieldPreviews = {
                      campaign_id : 1838979,
                      affiliate_id : 142542,
                      campaign_name: 'TestCampaign',
                      affiliate_name: 'TestAffiliate',
                      geo: 'US',
                      ip: '192.0.2.1',
                      click_date: '2025-06-26',
                      referrer: 'https://example.com',
                      sub_campaign: 'sub_campaign',
                      conversion_date: '2025-06-27',
                      redirect_url: 'https://redirect.example.com',
                      is_fired: 1,
                      payin: '100',
                      payout: '80',
                      lid: '69c4685c-1efa-4c60-82fd-f89a7cafd792',
                      cid: 'abc123',
                  };

                  for (let i = extraFields.length - 1; i >= 0; i--) {
                      const field = extraFields[i];
                      const newItem = {
                          isInclude: true,
                          column: field,
                          renamed_to: '',
                          preview: fieldPreviews[field] || '',
                      };
                      this.items.splice(1, 0, newItem);
                  }
              }
          },
          headers(newVal, oldVal) {},
        },
        computed: {
            ...mapGetters('affiliates', ['getAllAffiliates']),
            ...mapGetters('reports', ['getDrillDownReports', 'getReportsWithoutPagination', 'getExportData']),
            
            showBreakdownOption() {
              // Show breakdown option only for advertiser and affiliate reports without drilldown
              const validTypes = ['advertiser', 'affiliate'];
              if (!validTypes.includes(this.reportType)) {
                return false;
              }
              // Hide if drilldown is active (getExportData.value differs from reportType)
              return this.getExportData.value === this.reportType;
            },
            
            breakdownLabel() {
              if (this.reportType === 'advertiser') {
                return 'Affiliates';
              } else if (this.reportType === 'affiliate') {
                return 'Advertisers';
              }
              return '';
            },
            
            reportTypeLabel() {
              return this.reportType === 'advertiser' ? 'Advertiser' : 'Affiliate';
            },
        },
        methods:{
            download() {
              this.loading = true;
              
              const exportFields = this.items
                .filter(item => item.isInclude)
                .map(item => ({
                  column: item.column,
                  alias: item.renamed_to || ''
                }));
              
              if (this.enableBreakdown) {
                const breakdownValue = this.reportType === 'advertiser' ? 'affiliate' : 'advertiser';
                store.dispatch('reports/setBreakdownBy', breakdownValue);
                
                const breakdownField = this.reportType === 'advertiser' ? 'affiliate' : 'advertiser';
                if (!exportFields.some(f => f.column === breakdownField)) {
                  exportFields.splice(1, 0, { column: breakdownField, alias: '' });
                }
              } else {
                store.dispatch('reports/setBreakdownBy', null);
              }
              
              store.dispatch('reports/setExportFields', exportFields);
              
              this.$store.dispatch('alert/ALERT_INFO', 
                'Report is being generated and will be sent to your email shortly.');
              
              this.close();
              this.loading = false;
              
              store.dispatch('reports/exportReport', false);
            },

            setHeadersAndExample() {
                this.items.length = 0;
                const mappedColumns = {
                    campaigns: 'campaign',
                    affiliates: 'affiliate',
                    offers: 'offers',
                    programs: 'program',
                    advertisers: 'advertiser',
                    am: 'affiliate_manager',
                    bd: 'advertiser_manager',
                    verticals: 'vertical_name',
                };

                this.reportType = mappedColumns[this.headers[0]] || this.headers[0];

                if (!this.getExportData['value']) {
                    return this.items = [];
                }

                const headers = [this.getExportData['value'], ...Object.keys(this.getExportData).filter(k => ![this.getExportData.value, 'value'].includes(k))];

                headers.forEach(h => {
                    const item = {
                        isInclude: true,
                        column: h,
                        renamed_to: '',
                        preview:  this.getExportData[h]
                    };
                    this.items.push(item);
                });
            },

            close() {
                this.dialog = false;
                this.items = [];
                this.enableBreakdown = false;
            },

            csvEscape(val) {
                const s = (val === null || val === undefined) ? '' : String(val);
                const escaped = s.replace(/"/g, '""');
                return `"${escaped}"`;
            }
        }
    }
</script>

<style scoped lang="sass">
    .v-input
        padding: 0 !important
    .btn-section
        text-align: end
        margin: 2%
    .v-btn
        text-transform: capitalize !important
        min-width: 450px
    .v-btn__content
        color: black
    .container
        background-color: #f5f5f5
        padding: 0
    .theme--light.v-data-table
        background-color: #f5f5f5 !important
        padding-left: 5%
        padding-right: 5%
    .headText
        margin-top: 3%
        padding-left: 10%
        padding-right: 10%
        text-align: center
    .headSection
        padding-top: 2%
        display: flex
        justify-content: space-between
        align-items: center
    .centered-child
        margin-left: 40%
        font-weight: bold
        font-size: 16pt
        color: #3a3852

    .right-aligned-child
        font-weight: bold
        margin-left: auto
        font-size: 16pt
        margin-right: 3%
        &:hover
            cursor: pointer
</style>
